import React, { useState } from "react";
import { X, Gift, User, Package, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import Price from "../Currency/Price";
import { COUNTRY_CODES } from "../../constants/countryCodes";

interface GiftSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (giftData: GiftModalFormData) => void;
}
export interface GiftModalFormData {
  recipientName: string;
  recipientEmail: string;
  recipientPhone: string;
  message: string;
  giftWrapper: string;
  additionalRequirements: string;
  deliveryDate: string;
  quantity: number;
  shippingAddress: string;
  paymentMethod: string;
}

const GiftSendModal: React.FC<GiftSendModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const loading = false;
  const giftId: string | null = null;

  const [formData, setFormData] = useState<GiftModalFormData>({
    recipientName: "",
    recipientEmail: "",
    recipientPhone: "",
    message: "",
    giftWrapper: "standard",
    additionalRequirements: "",
    deliveryDate: "",
    quantity: 1,
    shippingAddress: "",
    paymentMethod: "",
  });

  const [errors, setErrors] = useState<Partial<GiftModalFormData>>({});
  const [countryCode, setCountryCode] = useState<string>("+974");
  const selectedCountry =
    COUNTRY_CODES.find((country) => country.code === countryCode) ||
    COUNTRY_CODES.find((country) => country.country === "QA") ||
    COUNTRY_CODES[0];
  const giftWrapperOptions = [
    {
      value: "standard",
      label: t("Standard Wrapper"),
      price: 0,
      color: "from-blue-400 to-blue-600",
    },
    {
      value: "premium",
      label: t("Premium Wrapper"),
      price: 5,
      color: "from-purple-400 to-purple-600",
    },
    {
      value: "luxury",
      label: t("Luxury Wrapper"),
      price: 10,
      color: "from-pink-400 to-pink-600",
    },
    {
      value: "custom",
      label: t("Custom Design"),
      price: 15,
      color: "from-orange-400 to-orange-600",
    },
  ];

  const validateStep1 = (): boolean => {
    const newErrors: Partial<GiftModalFormData> = {};

    if (!formData.recipientName.trim()) {
      newErrors.recipientName = t("Recipient name is required");
    }

    const sanitizedPhone = formData.recipientPhone.replace(/\D/g, "");
    const phoneRegex = /^[0-9]{6,15}$/;
    if (!formData.recipientPhone.trim()) {
      newErrors.recipientPhone = t("Phone number is required");
    } else if (!phoneRegex.test(sanitizedPhone)) {
      newErrors.recipientPhone = t("Please enter a valid phone number");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // const handleNext = () => {
  //   if (step === 1 && validateStep1()) {
  //     setStep(2);
  //   } else if (step === 2) {
  //     setStep(3);
  //   }
  // };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  //   setLoading(true);
  //   try {
  //     const selectedWrapper = giftWrapperOptions.find(w => w.value === formData.giftWrapper);
  //     const totalAmount = productPrice + (selectedWrapper?.price || 0);

  //     const payload = {
  //       productId,
  //       productName,
  //       recipientName: formData.recipientName,
  //       recipientPhone: formData.recipientPhone,
  //       message: formData.message,
  //       quantity: formData.quantity,
  //       shippingAddress: formData.shippingAddress,
  //       paymentMethod: formData.paymentMethod,
  //       amount: totalAmount,
  //       giftWrapper: formData.giftWrapper,
  //       additionalRequirements: formData.additionalRequirements,
  //       deliveryDate: formData.deliveryDate || undefined
  //     };

  //     const res = await BaseApi.post('/gifts/send', payload);

  //     if (res.success) {
  //       setGiftId(res.data?.id || res.giftId);
  //       success(t('Gift sent successfully!'));
  //       setStep(4); // Success step
  //     } else {
  //       err(res.message || t('Failed to send gift'));
  //     }
  //   } catch (error: any) {
  //     err(error.message || t('An error occurred while sending the gift'));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleChange = (field: keyof GiftModalFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = () => {
    if (!validateStep1()) return;

    onSuccess({
      ...formData,
      recipientPhone: `${countryCode}${formData.recipientPhone.replace(/\D/g, "")}`,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
      style={{ margin: 0 }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          zIndex: 10000,
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-700 to-black text-white p-6 rounded-t-3xl sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Gift className="w-8 h-8" />
              <div>
                <h3 className="text-2xl font-bold">{t("Send as Gift")}</h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-pink-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 flex items-center gap-2">
            {[1].map((stepNum) => (
              <div
                key={stepNum}
                className={`flex-1 h-2 rounded-full transition-all bg-pink-30"`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Recipient Details */}
          {step === 1 && (
            <div className="space-y-4 animate-slide-in">
              <h4 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-pink-500" />
                {t("Recipient Details")}
              </h4>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Recipient Name")} *
                </label>
                <input
                  type="text"
                  value={formData.recipientName}
                  onChange={(e) =>
                    handleChange("recipientName", e.target.value)
                  }
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 ${
                    errors.recipientName ? "border-red-500" : "border-gray-200"
                  }`}
                  placeholder={t("Enter recipient name")}
                />
                {errors.recipientName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.recipientName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Phone Number")} *
                </label>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <label className="relative sm:w-72">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="w-full appearance-none rounded-xl border-2 border-gray-200 bg-white px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    >
                      {COUNTRY_CODES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-gray-400">
                      ▾
                    </span>
                  </label>
                  <input
                    type="tel"
                    value={formData.recipientPhone}
                    onChange={(e) =>
                      handleChange("recipientPhone", e.target.value)
                    }
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 ${
                      errors.recipientPhone
                        ? "border-red-500"
                        : "border-gray-200"
                    }`}
                    placeholder={`${t("Enter phone number")} (${selectedCountry.code})`}
                    maxLength={15}
                  />
                </div>
                {errors.recipientPhone && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.recipientPhone}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Gift Message")} ({t("Optional")})
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder={t("gift-message")}
                  rows={4}
                  maxLength={200}
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.message.length}/200
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Gift Options */}
          {step === 2 && (
            <div className="space-y-4 animate-slide-in">
              <h4 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-500" />
                {t("Gift Wrapper & Options")}
              </h4>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t("Select Gift Wrapper")}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {giftWrapperOptions.map((wrapper) => (
                    <button
                      key={wrapper.value}
                      onClick={() => handleChange("giftWrapper", wrapper.value)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        formData.giftWrapper === wrapper.value
                          ? "border-pink-500 bg-pink-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div
                        className={`w-full h-20 rounded-lg bg-gradient-to-br ${wrapper.color} mb-2`}
                      />
                      <div className="text-sm font-semibold text-gray-900">
                        {wrapper.label}
                      </div>
                      <div className="text-xs text-gray-600">
                        {wrapper.price === 0 ? (
                          t("Free")
                        ) : (
                          <Price amount={wrapper.price || 0} />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Delivery Date")} ({t("Optional")})
                </label>
                <input
                  type="date"
                  value={formData.deliveryDate}
                  onChange={(e) => handleChange("deliveryDate", e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Additional Requirements")} ({t("Optional")})
                </label>
                <textarea
                  value={formData.additionalRequirements}
                  onChange={(e) =>
                    handleChange("additionalRequirements", e.target.value)
                  }
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder={t("Any special instructions...")}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="space-y-4 animate-slide-in">
              <h4 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-pink-500" />
                {t("Confirm Gift Details")}
              </h4>

              <div className="bg-white rounded-xl p-4 border-2 border-gray-200 space-y-2">
                <div>
                  <span className="text-sm text-gray-600">{t("To")}: </span>
                  <span className="font-semibold">
                    {formData.recipientName}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">{t("Phone")}: </span>
                  <span className="font-semibold">
                    {countryCode}
                    {formData.recipientPhone.replace(/\D/g, "")}
                  </span>
                </div>
                {formData.message && (
                  <div>
                    <span className="text-sm text-gray-600">
                      {t("Message")}:{" "}
                    </span>
                    <p className="text-sm italic text-gray-700 mt-1">
                      "{formData.message}"
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && giftId && (
            <div className="text-center space-y-4 animate-slide-in py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Gift className="w-10 h-10 text-green-600" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900">
                {t("Gift Sent Successfully!")}
              </h4>
              <p className="text-gray-600">{t("Your gift is on its way!")}</p>
              <div className="bg-gray-100 rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-1">{t("Tracking ID")}</p>
                <p className="text-lg font-bold text-gray-900">{giftId}</p>
              </div>
              <button
                onClick={() => window.open(`/gifts/track/${giftId}`, "_blank")}
                className="bg-gradient-to-r from-gray-700 to-black text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all font-semibold"
              >
                {t("Track Gift")}
              </button>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 4 && (
            <div className="flex gap-3 mt-6">
              {step > 1 && (
                <button
                  onClick={handleBack}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 font-semibold transition-colors"
                >
                  {t("Back")}
                </button>
              )}
              <button
                onClick={() => (step === 1 ? handleSubmit() : null)}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-gray-700 to-black text-white rounded-xl hover:shadow-lg transition-all font-semibold disabled:opacity-50"
              >
                {loading
                  ? t("Sending...")
                  : step === 1
                    ? t("Send Gift")
                    : t("Next")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftSendModal;
